import { getAppDirectory } from "@/config/mcp-app-directories";

/**
 * Where every "Book a demo" / "Book a call" CTA goes. The form verifies a work
 * email and picks the calendar by research type, so no CTA links a Cal page
 * directly: that skips both.
 */
export const BOOK_A_CALL_HREF = "/researchers/login";

/**
 * Cal.com event slugs, not URLs: the inline embed on /researchers/login takes
 * the bare "user/event" form. AI training books with Zac.
 */
export const DEMO_BOOKING_CAL_LINK = "zac/terac";

/** Everything that is not AI training books with sales rather than with Zac. */
export const SALES_BOOKING_CAL_LINK = "gabriella-riche/terac-intro-call";

export const MCP_INSTALL_GUIDE_URL = "/docs/researchers/mcp/install";

/**
 * Kept as a named re-export because several merged call sites import it from
 * here. The value itself now lives in the app-directory registry, so ChatGPT and
 * Claude cannot drift apart.
 */
export const CHATGPT_APP_URL = getAppDirectory("chatgpt").listingUrl;
export const CLAUDE_APP_URL = getAppDirectory("claude").listingUrl;

export interface NavLink {
  href: string;
  name: string;
  /** Shown under the link inside header dropdown panels. */
  description?: string;
  external?: boolean;
  /**
   * Promote this link into the header. Absent means footer-only, which is the
   * default on purpose: a new marketing page reaches the footer index by being
   * added to `NAV` at all, and reaching the header is a separate, deliberate
   * decision. The header is the set of paths a stranger needs to decide; the
   * footer is the complete index for someone who already knows what they want.
   */
  inHeader?: true;
}

export interface NavSection {
  /** Column heading inside a header dropdown. Not rendered in the footer. */
  label: string;
  links: NavLink[];
}

export interface NavGroup {
  /** Header dropdown trigger, and the footer column heading. */
  label: string;
  sections: NavSection[];
}

/**
 * The only place the marketing site declares a navigable surface.
 *
 * Header, footer and the mobile menu are all projections of this array, so a
 * page cannot end up reachable from one and not the other by accident. Before
 * this existed the header and footer were independent literals in two files,
 * which is how /enterprise, /desktop, /compare, /team, /verify and
 * /data-partnerships ended up linked from neither.
 *
 * Adding a marketing route means adding it here. Order is render order.
 */
export const NAV: NavGroup[] = [
  {
    label: "Platform",
    sections: [
      {
        label: "Access",
        links: [
          {
            href: "/researchers",
            name: "Overview",
            description: "How the verified expert network works",
            inHeader: true,
          },
          {
            href: "/enterprise",
            name: "Enterprise",
            description: "SSO, SOC 2, and dedicated support",
            inHeader: true,
          },
        ],
      },
      {
        label: "Interfaces",
        links: [
          {
            href: "/claude",
            name: "Claude App",
            description: "Recruit and interview real people inside Claude",
            inHeader: true,
          },
          {
            href: "/chatgpt",
            name: "ChatGPT App",
            description: "Recruit and interview real people inside ChatGPT",
            inHeader: true,
          },
          {
            href: "/mcp",
            name: "Agent MCP",
            description: "Verified human labor for AI agents, one install",
            inHeader: true,
          },
          {
            href: "/docs/developers",
            name: "Terac API",
            description: "Source, hire, verify, and pay programmatically",
            inHeader: true,
          },
        ],
      },
    ],
  },
  {
    label: "Solutions",
    sections: [
      {
        label: "AI Labs",
        links: [
          {
            href: "/ai",
            name: "AI Training",
            description: "Human data for AI training and evaluation",
            inHeader: true,
          },
        ],
      },
      {
        label: "Research Teams",
        links: [
          {
            href: "/market-research",
            name: "Market Research",
            description: "Panel infrastructure for research platforms",
            inHeader: true,
          },
          {
            href: "/user-research",
            name: "User Research",
            description: "Recruit verified users for product feedback",
            inHeader: true,
          },
          {
            href: "/academic-research",
            name: "Academic Research",
            description: "Verified participants for surveys and experiments",
            inHeader: true,
          },
        ],
      },
    ],
  },
  {
    label: "Experts",
    sections: [
      {
        label: "Find work",
        links: [
          {
            href: "/opportunities",
            name: "Find Opportunities",
            description: "Browse open research and AI projects",
            inHeader: true,
          },
          {
            href: "/assessments",
            name: "Assessments",
            description: "Rank on real work, checked by two peers",
            inHeader: true,
          },
          {
            href: "/referrals",
            name: "Referrals",
            description: "Earn by introducing experts and companies",
            inHeader: true,
          },
          {
            href: "/desktop",
            name: "Desktop App",
            description: "Record task demonstrations on macOS and Windows",
          },
        ],
      },
      {
        label: "Community",
        links: [
          {
            href: "/stories",
            name: "Expert Stories",
            description: "How experts use Terac, in their own words",
            inHeader: true,
          },
          { href: "/help", name: "Help for Experts" },
          // Experts are the side of the marketplace that gets phished with fake
          // recruiter messages, so the domain checker belongs in their index
          // rather than with the company trust pages.
          { href: "/verify", name: "Verify a Terac domain" },
        ],
      },
    ],
  },
  {
    label: "Resources",
    sections: [
      {
        label: "Documentation",
        links: [
          {
            href: "/docs/experts",
            name: "For Experts",
            description: "Applying, earning, and getting paid",
            inHeader: true,
          },
          {
            href: "/docs/researchers",
            name: "For Researchers",
            description: "Opportunities, results, and integrations",
            inHeader: true,
          },
          {
            href: "/docs/developers",
            name: "For Developers",
            description: "API reference, guides, and MCP",
            inHeader: true,
          },
        ],
      },
      {
        label: "Learn",
        links: [
          {
            href: "/blog",
            name: "Blog",
            description: "What we learn, hire, and believe",
            inHeader: true,
          },
          {
            href: "/whats-new",
            name: "What's New",
            description: "Every feature, improvement, and fix we ship",
            inHeader: true,
          },
          {
            href: "/customers",
            name: "Customer Stories",
            description: "How teams run research on Terac",
            inHeader: true,
          },
          { href: "/events", name: "Events" },
          { href: "/resources/faq", name: "FAQ" },
        ],
      },
    ],
  },
  {
    label: "Company",
    sections: [
      {
        label: "Company",
        links: [
          { href: "/about", name: "About" },
          { href: "/team", name: "Team" },
          { href: "/about#roles", name: "Careers" },
          { href: "/partners", name: "Partner with us" },
          { href: "/data-partnerships", name: "Data Partnerships" },
          { href: "/brand", name: "Brand & Press" },
          { href: "/contact", name: "Contact us" },
          { href: "/legal", name: "Legal" },
          {
            href: "https://github.com/TeracAI",
            name: "GitHub",
            external: true,
          },
        ],
      },
    ],
  },
];

/**
 * `NAV` narrowed to the links flagged `inHeader`, with empty sections and
 * empty groups dropped. Groups keep their `NAV` order, so the header trigger
 * order follows the footer column order for free.
 */
export const headerGroups: NavGroup[] = NAV.map((group) => ({
  ...group,
  sections: group.sections
    .map((section) => ({
      ...section,
      links: section.links.filter((link) => link.inHeader),
    }))
    .filter((section) => section.links.length > 0),
})).filter((group) => group.sections.length > 0);

/** `NAV` flattened one level: one column per group, section labels dropped. */
export const footerSections = NAV.map((group) => ({
  title: group.label,
  items: group.sections.flatMap((section) => section.links),
}));

/** The header groups as mobile accordions. */
export const mobileNavLinks = headerGroups.map((group) => ({
  groupName: group.label,
  links: group.sections.flatMap((section) => section.links),
}));
